import * as XLSX from 'xlsx';
import moment from 'moment';

export interface ExportContext {
	section: string;       // 'users' | 'patrimony' | 'plans' | 'chat' | 'activity'
	sectionLabel: string;  // 'Usuarios' | 'Patrimonio' | ...
	filter: {
		since?: string;
		until?: string;
		period?: string;
	};
	data: any;             // metrics del backend
}

type Cell = string | number;
type SheetBlock = { title: string; rows: Cell[][] };

// ---------- Helpers ----------

const safeNum = (value: any): number => {
	const n = Number(value);
	return isNaN(n) ? 0 : n;
}

// formatPct: número -> "X.XX%"; string -> tal cual
function formatPct(value: any): string {
	if (value == null || value === '') return '';
	if (typeof value === 'number') return `${ value.toFixed(2) }%`;
	const s = String(value);
	return s.endsWith('%') ? s : s;
}

function formatMoney(value: any): string {
	const n = Number(value) || 0;
	return `$${ n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }`;
}

// Escapa una celda para CSV: comillas dobladas + quote si hay coma/salto/comilla
function escapeCsvCell(value: any): string {
	if (value == null) return '';
	const s = String(value);
	if (/[",\n\r]/.test(s)) {
		return `"${ s.replace(/"/g, '""') }"`;
	}
	return s;
}

// Nombre de sheet válido para Excel: máx 31 chars, sin : \ / ? * [ ]
function sanitizeSheetName(name: string): string {
	const cleaned = String(name).replace(/[:\\/?*[\]]/g, ' ').trim();
	return cleaned.slice(0, 31) || 'Hoja';
}

function triggerDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

// Filtro legible: si no hay since/until, usar el período (ej. "Todo")
function filterValue(ctx: ExportContext, key: 'since' | 'until'): string {
	const v = ctx.filter?.[key];
	if (v) return String(v);
	return ctx.filter?.period === 'all' ? 'Todo' : '';
}

function metaRows(ctx: ExportContext): Cell[][] {
	return [
		['Reporte Holdz Finance'],
		['Sección', ctx.sectionLabel],
		['Filtro Desde', filterValue(ctx, 'since')],
		['Filtro Hasta', filterValue(ctx, 'until')],
		['Período', ctx.filter?.period || ''],
		['Generado', moment().format('YYYY-MM-DD HH:mm:ss')],
	];
}

// ---------- Builders por sección ----------

function buildUsersData(d: any): SheetBlock[] {
	const m = d || {};
	const ghost = m.ghost_users || {};
	const sub = m.subusers || {};
	const tops = Array.isArray(sub.top_parent_users) ? sub.top_parent_users : [];

	return [
		{
			title: 'Usuarios Fantasma',
			rows: [
				['Métrica', 'Valor'],
				['Cantidad', safeNum(ghost.count)],
				['Porcentaje del total', formatPct(ghost.pct)],
			],
		},
		{
			title: 'Subusuarios',
			rows: [
				['Métrica', 'Valor'],
				['Total subusuarios', safeNum(sub.total_subusers)],
				['Porcentaje del total con sub-cuentas', formatPct(sub.pct_subusers)],
			],
		},
		{
			title: 'Top Cuentas con Subusuarios',
			rows: [
				['#', 'Nombre', 'Apellido', 'Email', 'Subusuarios'],
				...tops.map((r: any, i: number) => [
					i + 1,
					r?.name || '',
					r?.lastname || '',
					r?.email || '',
					safeNum(r?.subusers_count),
				]),
			],
		},
	];
}

function buildPatrimonyData(d: any): SheetBlock[] {
	const m = d || {};
	const adopt = m.patrimony_adoption || {};
	const byPlan = m.patrimony_by_plan || {};
	const free = byPlan.free || {};
	const premium = byPlan.premium || {};

	return [
		{
			title: 'Adopción Patrimonio',
			rows: [
				['Métrica', 'Valor'],
				['Clientes con patrimonio', safeNum(adopt.with_patrimony)],
				['Total clientes', safeNum(adopt.total_clients)],
				['Adopción', formatPct(adopt.adoption_pct)],
			],
		},
		{
			title: 'Patrimonio por Plan',
			rows: [
				['Plan', 'Usuarios', 'Promedio', 'Total'],
				['Free', safeNum(free.users), formatMoney(free.avg_patrimony), formatMoney(free.sum_patrimony)],
				['Premium', safeNum(premium.users), formatMoney(premium.avg_patrimony), formatMoney(premium.sum_patrimony)],
			],
		},
	];
}

function buildPlansData(d: any): SheetBlock[] {
	const m = d || {};
	const conv = m.conversion_rates || {};
	const ttc = m.time_to_conversion || {};
	const funnel = m.funnel || {};
	const renewal = m.renewal_rate || {};
	const revenue = m.revenue || {};
	const byMonth = Array.isArray(revenue.by_month) ? revenue.by_month : [];
	const pvc = m.paid_vs_complimentary || {};
	const winback = Array.isArray(m.winback_candidates) ? m.winback_candidates : [];

	return [
		{
			title: 'Revenue',
			rows: [
				['Métrica', 'Valor'],
				['Revenue histórico', formatMoney(revenue.lifetime_revenue)],
				['Transacciones históricas', safeNum(revenue.lifetime_transactions)],
				[],
				['Mes', 'Revenue', 'Transacciones', 'Ticket Promedio'],
				...byMonth.map((r: any) => [
					r?.ym || '',
					formatMoney(r?.revenue),
					safeNum(r?.paid_transactions),
					formatMoney(r?.avg_ticket),
				]),
			],
		},
		{
			title: 'Tasas de Conversión',
			rows: [
				['Ventana', 'Conversiones', 'Tasa', 'Total Clientes'],
				['30 días', safeNum(conv.conv_30d), formatPct(conv.rate_30d_pct), safeNum(conv.total_clients)],
				['60 días', safeNum(conv.conv_60d), formatPct(conv.rate_60d_pct), safeNum(conv.total_clients)],
				['90 días', safeNum(conv.conv_90d), formatPct(conv.rate_90d_pct), safeNum(conv.total_clients)],
			],
		},
		{
			title: 'Tiempo a Conversión',
			rows: [
				['Métrica', 'Valor'],
				['Días promedio', safeNum(ttc.avg_days)],
				['Días mínimo', safeNum(ttc.min_days)],
				['Días máximo', safeNum(ttc.max_days)],
				['Conversiones totales', safeNum(ttc.conversions_total)],
			],
		},
		{
			title: 'Funnel',
			rows: [
				['Etapa', 'Valor'],
				['Registrados', safeNum(funnel.registered)],
				['Activos 30d', safeNum(funnel.active_30d)],
				['Premium activos', safeNum(funnel.premium_active)],
				['Tasa de activación', formatPct(funnel.activation_rate_pct)],
				['Tasa de conversión', formatPct(funnel.conversion_rate_pct)],
			],
		},
		{
			title: 'Renovaciones',
			rows: [
				['Métrica', 'Valor'],
				['Usuarios vencidos', safeNum(renewal.expired_users)],
				['Usuarios renovados', safeNum(renewal.renewed_users)],
				['Tasa de renovación', formatPct(renewal.renewal_rate_pct)],
			],
		},
		{
			title: 'Pago vs Cortesía',
			rows: [
				['Métrica', 'Valor'],
				['Cortesía activos', safeNum(pvc.complimentary_active)],
				['Pagados activos', safeNum(pvc.paid_active)],
				['% Pago real', formatPct(pvc.pct_real_paid)],
			],
		},
		{
			title: 'Winback Candidates',
			rows: [
				['Nombre', 'Apellido', 'Email', 'Vencimiento', 'Días Vencido'],
				...winback.map((u: any) => [
					u?.name || '',
					u?.lastname || '',
					u?.email || '',
					u?.expired_on ? moment(u.expired_on).format('YYYY-MM-DD') : '',
					safeNum(u?.days_expired),
				]),
			],
		},
	];
}

function buildChatData(d: any): SheetBlock[] {
	const m = d || {};
	const adopt = m.chat_adoption || {};
	const post = m.post_chat_conversion || {};
	const byPlan = m.chat_by_plan || {};
	const free = byPlan.free || {};
	const premium = byPlan.premium || {};

	return [
		{
			title: 'Adopción Chat IA',
			rows: [
				['Métrica', 'Valor'],
				['Usaron el chat alguna vez', safeNum(adopt.ever_used)],
				['Total clientes', safeNum(adopt.total_clients)],
				['Adopción', formatPct(adopt.adoption_pct)],
			],
		},
		{
			title: 'Conversión Post-Chat',
			rows: [
				['Métrica', 'Valor'],
				['Chatters Free', safeNum(post.free_chatters)],
				['Convirtieron después', safeNum(post.converted_after)],
				['% Conversión post-chat', formatPct(post.post_chat_conv_pct)],
			],
		},
		{
			title: 'Uso por Plan',
			rows: [
				['Plan', 'Chatters', 'Mensajes', 'Promedio por Usuario'],
				['Free', safeNum(free.chatters), safeNum(free.messages), safeNum(free.avg_per_user)],
				['Premium', safeNum(premium.chatters), safeNum(premium.messages), safeNum(premium.avg_per_user)],
			],
		},
	];
}

function buildActivityData(d: any): SheetBlock[] {
	const m = d || {};
	const dwm = m.dau_wau_mau || {};
	const stick = m.stickiness || {};
	const fa = m.feature_adoption || {};
	const feats = Array.isArray(fa.features) ? fa.features : [];

	return [
		{
			title: 'DAU WAU MAU',
			rows: [
				['Métrica', 'Valor'],
				['DAU', safeNum(dwm.dau)],
				['WAU', safeNum(dwm.wau)],
				['MAU', safeNum(dwm.mau)],
			],
		},
		{
			title: 'Stickiness',
			rows: [
				['Métrica', 'Valor'],
				['Stickiness', formatPct(stick.stickiness_pct)],
				['Nivel', stick.level || ''],
			],
		},
		{
			title: 'Adopción por Feature',
			rows: [
				['Feature', 'Usuarios', 'Porcentaje', 'Total Clientes'],
				...feats.map((f: any) => [
					f?.name || '',
					safeNum(f?.users),
					formatPct(f?.pct),
					safeNum(fa.total_clients),
				]),
			],
		},
	];
}

function buildSectionData(section: string, data: any): SheetBlock[] {
	if (section === 'users') return buildUsersData(data);
	if (section === 'patrimony') return buildPatrimonyData(data);
	if (section === 'plans') return buildPlansData(data);
	if (section === 'chat') return buildChatData(data);
	if (section === 'activity') return buildActivityData(data);
	return [];
}

// ---------- Export públicos ----------

export function exportToCSV(ctx: ExportContext): void {
	const lines: string[] = [];

	// METADATA
	metaRows(ctx).forEach((r) => {
		lines.push(r.map(escapeCsvCell).join(','));
	});
	lines.push('');

	// SECCIONES
	const blocks = buildSectionData(ctx.section, ctx.data);
	blocks.forEach(({ title, rows }) => {
		lines.push(escapeCsvCell(title));
		rows.forEach((row) => {
			lines.push(row.map(escapeCsvCell).join(','));
		});
		lines.push('');
	});

	// BOM UTF-8 para que Excel reconozca acentos
	const csvContent = '﻿' + lines.join('\n');
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
	triggerDownload(blob, `reporte-${ ctx.section }-${ moment().format('YYYYMMDD-HHmmss') }.csv`);
}

export function exportToExcel(ctx: ExportContext): void {
	const wb = XLSX.utils.book_new();

	// METADATA SHEET
	const metaSheet = XLSX.utils.aoa_to_sheet(metaRows(ctx));
	XLSX.utils.book_append_sheet(wb, metaSheet, 'Metadata');

	// SHEETS POR MÉTRICA
	const usedNames = new Set<string>(['Metadata']);
	const blocks = buildSectionData(ctx.section, ctx.data);

	blocks.forEach(({ title, rows }) => {
		const sheet = XLSX.utils.aoa_to_sheet(rows.length ? rows : [[]]);
		let name = sanitizeSheetName(title);
		// Excel no permite nombres de sheet duplicados
		let suffix = 1;
		while (usedNames.has(name)) {
			const base = sanitizeSheetName(title).slice(0, 28);
			name = `${ base } ${ ++suffix }`;
		}
		usedNames.add(name);
		XLSX.utils.book_append_sheet(wb, sheet, name);
	});

	XLSX.writeFile(wb, `reporte-${ ctx.section }-${ moment().format('YYYYMMDD-HHmmss') }.xlsx`);
}
